import type { FutureRevenueProjection, RevenueBucket } from '@salonomy/types';
import { Field, Pill, SegmentedControl, Select, Stat } from '@salonomy/ui';
import { useState } from 'react';
import { useFutureRevenueProjection } from '../../data/reports.js';
import { exportCsvFilename } from '../../lib/csv.js';
import { money, moneyWhole } from '../../lib/format.js';
import {
  FUTURE_HORIZON_OPTIONS,
  ReportCard,
  type CsvExport,
  type UseFutureHorizon,
} from './shared.js';

/**
 * WS9 HERO — the future-revenue projection, the headline of the Reports tab and the FIX for the old
 * mis-sourced client-side projection (which summed /v1/appointments priceQuoted in JS). EVERY figure here is
 * the SERVER's: `expectedPence` is gross discounted by the derived/overridden no-show rate (ADR-0009 money
 * honesty — the projection is NEVER recomputed in the browser; we only render report.* and the buckets).
 */

/** How the server labels the no-show rate it used — surfaced honestly so the figure is auditable. */
const NO_SHOW_SOURCE_LABEL: Record<FutureRevenueProjection['noShowRateSource'], string> = {
  computed: 'from this salon’s own history',
  default: 'platform default (not enough history yet)',
  override: 'a manual override',
};

/** 0–1 float → "12.5%" for the one projection field that is a float, not basis points. */
function noShowPct(rate: number): string {
  const pct = rate * 100;
  return `${Number.isInteger(pct) ? pct : pct.toFixed(1)}%`;
}

export function FutureRevenueCard({ control }: { control: UseFutureHorizon }) {
  const { range, horizonWeeks, setHorizonWeeks } = control;
  const [grain, setGrain] = useState<'week' | 'month'>('week');
  const { data: report, isLoading, isError } = useFutureRevenueProjection(range, horizonWeeks);

  const buckets: RevenueBucket[] = report
    ? grain === 'week'
      ? report.byWeek
      : report.byMonth
    : [];
  const isEmpty = !!report && buckets.length === 0;

  // CSV is a faithful copy of the on-screen buckets — money cells pre-formatted via money() (lib/csv.ts).
  const csv: CsvExport | null =
    report && buckets.length > 0
      ? {
          filename: exportCsvFilename(`future-revenue-by-${grain}`),
          headers: [
            grain === 'week' ? 'Week' : 'Month',
            'Period start',
            'Period end',
            'Gross',
            'Expected (after no-show)',
            'Appointments',
          ],
          rows: buckets.map((b) => [
            b.key,
            b.periodStart,
            b.periodEnd,
            money(b.grossPence),
            money(b.expectedPence),
            b.appointmentCount,
          ]),
        }
      : null;

  return (
    <ReportCard
      title="Future revenue projection"
      subtitle="Booked &amp; confirmed appointments ahead, discounted by the no-show rate. Every figure is server-computed — this is the salon’s expected take, not a browser estimate."
      isLoading={isLoading}
      isError={isError}
      isEmpty={isEmpty}
      emptyLabel="No upcoming booked revenue in this window."
      csv={csv}
      headerExtra={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Field label="Horizon">
            <Select
              value={String(horizonWeeks)}
              onChange={(e) => setHorizonWeeks(Number(e.target.value))}
            >
              {FUTURE_HORIZON_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </Field>
          <SegmentedControl
            options={[
              { value: 'week' as const, label: 'By week' },
              { value: 'month' as const, label: 'By month' },
            ]}
            value={grain}
            onChange={setGrain}
          />
        </div>
      }
    >
      {report ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <span className="slm-stat__label">Expected revenue · projection</span>
            <div
              style={{
                fontFamily: 'var(--slm-font-display)',
                fontSize: 44,
                lineHeight: 1.05,
                marginTop: 4,
              }}
            >
              {moneyWhole(report.expectedPence)}
            </div>
            <div className="muted">
              {moneyWhole(report.grossPence)} booked gross, discounted by a{' '}
              {noShowPct(report.noShowRate)} no-show rate (
              {NO_SHOW_SOURCE_LABEL[report.noShowRateSource]}){' · '}
              {report.horizonWeeks}-week horizon
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <Pill tone="soft">{noShowPct(report.noShowRate)} no-show rate</Pill>
            <Pill tone="neutral">{report.noShowRateSource}</Pill>
          </div>

          <div className="settings-list">
            {buckets.map((b) => (
              <div key={b.key} className="settings-row">
                <div>
                  <div style={{ fontWeight: 600 }}>{b.key}</div>
                  <div className="muted" style={{ fontSize: 12.5 }}>
                    {b.periodStart} → {b.periodEnd} · {b.appointmentCount} appt
                    {b.appointmentCount === 1 ? '' : 's'} · {money(b.grossPence)} gross
                  </div>
                </div>
                <Stat label="Expected" value={money(b.expectedPence)} />
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </ReportCard>
  );
}
